import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import * as echarts from 'echarts';
import type { EChartsType } from 'echarts';

import { cn } from '../lib/utils';
import { apiFetch } from '../lib/api';
import { recordBadges } from '../config/recordBadges';

const summaryColumns = [
  { key: 'nickname', label: 'Ник' },
  { key: 'attendance', label: 'Посещаемость' },
  { key: 'frags', label: 'Фраги' },
  { key: 'tk', label: 'ТК' },
  { key: 'survival', label: 'Выживаемость' },
  { key: 'med', label: 'Медицина' },
  { key: 'deaths', label: 'Смерти' },
  { key: 'tkDeaths', label: 'Смерти от ТК' },
  { key: 'ka', label: 'K/A' },
  { key: 'kd', label: 'K/D' },
] as const;

export type SquadSummaryRow = {
  squad: string;
  nickname: string;
  attendance: number;
  frags: number;
  tk: number;
  survival: number;
  med: number;
  deaths: number;
  tkDeaths: number;
  ka: number;
  kd: number;
  recordType?: string | null;
};

type SquadAchievement = {
  type: string;
  description: string;
  season?: string;
};

type SquadDetailResponse = {
  team: string;
  summary: SquadSummaryRow[];
  kpd: { labels: string[]; values: number[] };
  frags: { labels: string[]; values: number[]; tkValues: number[] };
  attendance: {
    history: { mission: string; map: string; server: string; date: string; attendance: number }[];
    labels: string[];
    series: number[];
  };
  achievements: SquadAchievement[];
  contactInteractions: Array<{ opponent: string; kills: number; deaths: number }>;
};

export type SquadProfile = {
  summary: SquadSummaryRow[];
  kpdTrend: number[];
  fragsPerGame: number[];
  tkPerGame: number[];
  games: string[];
  attendanceHistory: { mission: string; map: string; server: string; date: string; attendance: number }[];
  attendanceSeries: number[];
  contactInteractions: Array<{ opponent: string; kills: number; deaths: number }>;
};

const squadProfiles: Record<string, SquadProfile> = {
  tor: {
    summary: [
      { squad: 'TOR', nickname: 'Veslo', attendance: 2, frags: 5, tk: 0, survival: 1, med: 0, deaths: 1, tkDeaths: 0, ka: 2.5, kd: 5 },
      { squad: 'TOR', nickname: 'Todor', attendance: 2, frags: 5, tk: 0, survival: 1, med: 0, deaths: 1, tkDeaths: 0, ka: 2.5, kd: 5 },
      { squad: 'TOR', nickname: 'Vitan', attendance: 2, frags: 6, tk: 0, survival: 0, med: 0, deaths: 2, tkDeaths: 0, ka: 3, kd: 3 },
      { squad: 'TOR', nickname: 'Evpator', attendance: 2, frags: 3, tk: 0, survival: 0, med: 0, deaths: 2, tkDeaths: 0, ka: 1.5, kd: 1.5 },
      { squad: 'TOR', nickname: 'Lokrad', attendance: 2, frags: 2, tk: 0, survival: 0, med: 0.018, deaths: 2, tkDeaths: 0, ka: 1, kd: 1 },
      { squad: 'TOR', nickname: 'vanga', attendance: 2, frags: 2, tk: 0, survival: 0, med: 0, deaths: 2, tkDeaths: 0, ka: 1, kd: 1 },
      { squad: 'TOR', nickname: 'sowok', attendance: 2, frags: 1, tk: 0, survival: 0, med: 0, deaths: 2, tkDeaths: 0, ka: 0.5, kd: 0.5 },
      { squad: 'TOR', nickname: 'ericson', attendance: 1, frags: 0, tk: 0, survival: 1, med: 0, deaths: 0, tkDeaths: 0, ka: 0, kd: 0 },
      { squad: 'TOR', nickname: 'XoTa6bI4', attendance: 2, frags: 0, tk: 0, survival: 0, med: 0, deaths: 2, tkDeaths: 0, ka: 0, kd: 0 },
    ],
    kpdTrend: [1.4, 1.52, 1.63, 1.58, 1.72, 1.68, 1.75, 1.81, 1.86],
    fragsPerGame: [12, 14, 10, 18, 16, 12, 20, 22, 19],
    tkPerGame: [1, 0, 0, 1, 0, 0, 1, 0, 0],
    games: ['Игра 1', 'Игра 2', 'Игра 3', 'Игра 4', 'Игра 5', 'Игра 6', 'Игра 7', 'Игра 8', 'Игра 9'],
    attendanceHistory: [
      { mission: '196_Rch_Repair_zone_v6', map: 'Enoch', server: 'T2', date: '12-12-25 23:39:28', attendance: 8 },
      { mission: '196_te_wybuchy_z_tylu_v10', map: 'chernarus', server: 'T2', date: '12-12-25 21:04:48', attendance: 9 },
      { mission: '196_frost_fire', map: 'vt7', server: 'T1', date: '10-12-25 21:15:20', attendance: 7 },
      { mission: '196_misty_ops', map: 'Altis', server: 'T1', date: '08-12-25 20:55:00', attendance: 10 },
    ],
    attendanceSeries: [8, 9, 7, 10, 9, 8, 11, 10, 9],
    contactInteractions: [
      { opponent: 'DWI', kills: 20, deaths: 14 },
      { opponent: 'DELTA', kills: 16, deaths: 10 },
      { opponent: 'KPblM', kills: 12, deaths: 18 },
      { opponent: 'MERCY', kills: 9, deaths: 7 },
      { opponent: 'SSG', kills: 14, deaths: 12 },
      { opponent: 'RM', kills: 11, deaths: 15 },
    ],
  },
};

type SquadAward = { icon: string; title: string; description: string; season?: string; placementPriority: number };

const achievementIcons: Record<string, string> = {
  sqd_1: '/icons/sqd-gold.svg',
  sqd_2: '/icons/sqd-silver.svg',
  sqd_3: '/icons/sqd-bronze.svg',
};

const achievementTitles: Record<string, string> = {
  sqd_1: '1 место',
  sqd_2: '2 место',
  sqd_3: '3 место',
};

const makeFallbackProfile = (name: string): SquadProfile => ({
  summary: [
    { squad: name, nickname: `${name}-Alpha`, attendance: 2, frags: 6, tk: 0, survival: 1, med: 0.01, deaths: 1, tkDeaths: 0, ka: 3, kd: 6 },
    { squad: name, nickname: `${name}-Bravo`, attendance: 2, frags: 5, tk: 0, survival: 1, med: 0.0, deaths: 1, tkDeaths: 0, ka: 2.5, kd: 5 },
    { squad: name, nickname: `${name}-Charlie`, attendance: 2, frags: 4, tk: 0, survival: 0, med: 0.0, deaths: 2, tkDeaths: 0, ka: 2.0, kd: 2.0 },
    { squad: name, nickname: `${name}-Delta`, attendance: 2, frags: 3, tk: 0, survival: 0, med: 0.0, deaths: 2, tkDeaths: 0, ka: 1.5, kd: 1.5 },
  ],
  kpdTrend: [1.1, 1.2, 1.0, 1.3, 1.25],
  fragsPerGame: [10, 12, 9, 14, 11],
  tkPerGame: [1, 0, 1, 0, 0],
  games: ['Игра 1', 'Игра 2', 'Игра 3', 'Игра 4', 'Игра 5'],
  attendanceHistory: [
    { mission: 'Отработка', map: 'Altis', server: 'T1', date: '01-12-25 20:00:00', attendance: 8 },
    { mission: 'Учения', map: 'Chernarus', server: 'T2', date: '03-12-25 20:30:00', attendance: 9 },
  ],
  attendanceSeries: [8, 9, 7, 10, 9],
  contactInteractions: [
    { opponent: 'DELTA', kills: 10, deaths: 12 },
    { opponent: 'DWI', kills: 8, deaths: 6 },
    { opponent: 'MERCY', kills: 6, deaths: 9 },
    { opponent: 'SSG', kills: 5, deaths: 5 },
  ],
});

function useChart(theme: 'light' | 'dark') {
  const instance = useRef<EChartsType | null>(null);
  const resizeHandler = useRef<(() => void) | null>(null);
  const [readyTick, setReadyTick] = useState(0);

  const ref = useCallback((node: HTMLDivElement | null) => {
    if (resizeHandler.current) {
      window.removeEventListener('resize', resizeHandler.current);
      resizeHandler.current = null;
    }

    if (instance.current) {
      instance.current.dispose();
      instance.current = null;
    }

    if (!node) return;

    instance.current = echarts.init(node);
    resizeHandler.current = () => instance.current?.resize();
    window.addEventListener('resize', resizeHandler.current);
    setReadyTick((value) => value + 1);
  }, [theme]);

  const setOption = (option: echarts.EChartsOption) => {
    if (!instance.current) return;
    instance.current.setOption(option, true);
    instance.current.resize();
  };

  const styles = useMemo(() => {
    const tokens = getComputedStyle(document.documentElement);
    const toHsl = (token: string, fallback: string) => {
      const value = tokens.getPropertyValue(token).trim();
      return value ? `hsl(${value})` : fallback;
    };

    return {
      foreground: toHsl('--foreground', '#e5e7eb'),
      muted: toHsl('--muted-foreground', '#9ca3af'),
      border: toHsl('--border', '#2a2d31'),
      card: toHsl('--card', theme === 'dark' ? '#1a1d21' : '#ffffff'),
      positive: toHsl('--success', '#27ae60'),
      negative: toHsl('--destructive', '#eb5757'),
      accent: toHsl('--accent', '#ffde00'),
    };
  }, [theme]);

  return { ref, setOption, styles, readyTick } as const;
}

export function SquadDetailPage({
  squad,
  theme,
  onBack,
  onNavigatePlayer,
}: {
  squad: string;
  theme: 'light' | 'dark';
  onBack: () => void;
  onNavigatePlayer?: (nickname: string) => void;
}): JSX.Element {
  const { data: squadDetail } = useQuery({
    queryKey: ['squad-detail', squad],
    queryFn: () => apiFetch<SquadDetailResponse>(`/squad-stats/${encodeURIComponent(squad)}/`),
    enabled: Boolean(squad),
  });
  const [tab, setTab] = useState<'summary' | 'attendance'>('summary');
  const [sortKey, setSortKey] = useState<keyof SquadSummaryRow>('kd');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const normalized = squad.toLowerCase();
  const squadName = squad.toUpperCase();
  const profile = squadProfiles[normalized] ?? makeFallbackProfile(squadName);
  const isDeltaSquad = squadName === 'DELTA';
  const trophies = useMemo(
    () => {
      const awards: SquadAward[] = (squadDetail?.achievements ?? []).map((achievement) => ({
        icon: achievementIcons[achievement.type] ?? '/icons/sqd-gold.svg',
        title: achievementTitles[achievement.type] ?? 'Награда',
        description: achievement.description,
        season: achievement.season,
        placementPriority:
          achievement.type === 'sqd_1' ? 1 : achievement.type === 'sqd_2' ? 2 : achievement.type === 'sqd_3' ? 3 : 4,
      }));

      if (isDeltaSquad) {
        awards.push({
          icon: '/icons/d_mad.svg',
          title: 'Operation AR',
          description: 'За успешное проведение операции "Абсолютная решимость"',
          placementPriority: 0,
        });
      }

      return awards.sort((left, right) => left.placementPriority - right.placementPriority);
    },
    [isDeltaSquad, squadDetail?.achievements]
  );

  const summaryRows = squadDetail?.summary ?? profile.summary;
  const sortedSummaryRows = useMemo(() => {
    const rows = [...summaryRows];
    rows.sort((left, right) => {
      const leftValue = left[sortKey];
      const rightValue = right[sortKey];
      if (typeof leftValue === 'string' || typeof rightValue === 'string') {
        const result = String(leftValue).localeCompare(String(rightValue), 'ru', { sensitivity: 'base' });
        return sortDirection === 'asc' ? result : -result;
      }
      const result = Number(leftValue) - Number(rightValue);
      return sortDirection === 'asc' ? result : -result;
    });
    return rows;
  }, [summaryRows, sortDirection, sortKey]);

  const kpdLabels = squadDetail?.kpd.labels ?? profile.games;
  const kpdValues = squadDetail?.kpd.values ?? profile.kpdTrend;
  const kpdChartLabels = useMemo(() => [...kpdLabels].reverse(), [kpdLabels]);
  const kpdChartValues = useMemo(() => [...kpdValues].reverse(), [kpdValues]);
  const fragsLabels = squadDetail?.frags.labels ?? profile.games;
  const fragsValues = squadDetail?.frags.values ?? profile.fragsPerGame;
  const tkValues = squadDetail?.frags.tkValues ?? profile.tkPerGame;
  const attendanceHistory = squadDetail?.attendance.history ?? profile.attendanceHistory;
  const attendanceLabels =
    squadDetail?.attendance.labels ?? attendanceHistory.map((row) => row.mission) ?? profile.games;
  const attendanceSeries =
    squadDetail?.attendance.series ?? attendanceHistory.map((row) => row.attendance) ?? profile.attendanceSeries;
  const attendanceChartLabels = useMemo(() => [...attendanceLabels].reverse(), [attendanceLabels]);
  const attendanceChartSeries = useMemo(() => [...attendanceSeries].reverse(), [attendanceSeries]);
  const contactInteractions = squadDetail?.contactInteractions ?? profile.contactInteractions;

  const kpdChart = useChart(theme);
  const fragsChart = useChart(theme);
  const attendanceChart = useChart(theme);
  const contactChart = useChart(theme);

  useEffect(() => {
    if (!kpdChartLabels.length) return;

    const { styles } = kpdChart;
    kpdChart.setOption({
      grid: { left: 48, right: 16, top: 32, bottom: 42 },
      xAxis: {
        type: 'category',
        data: kpdChartLabels,
        axisLine: { lineStyle: { color: styles.border } },
        axisLabel: { show: false, color: styles.muted },
      },
      yAxis: {
        type: 'value',
        axisLine: { lineStyle: { color: styles.border } },
        splitLine: { lineStyle: { color: styles.border, type: 'dashed' } },
        axisLabel: { color: styles.muted },
      },
      tooltip: { trigger: 'axis', backgroundColor: styles.card, borderColor: styles.border, textStyle: { color: styles.foreground } },
      series: [
        {
          type: 'line',
          data: kpdChartValues,
          smooth: true,
          symbol: 'circle',
          symbolSize: 8,
          lineStyle: { color: styles.accent, width: 3 },
          itemStyle: { color: styles.accent },
          areaStyle: { color: undefined },
        },
      ],
      animationDuration: 0,
    });
  }, [kpdChart, kpdChartLabels, kpdChartValues, theme, kpdChart.readyTick]);

  useEffect(() => {
    if (!fragsLabels.length) return;

    const { styles } = fragsChart;
    fragsChart.setOption({
      grid: { left: 48, right: 16, top: 32, bottom: 42 },
      xAxis: {
        type: 'category',
        data: fragsLabels,
        axisLine: { lineStyle: { color: styles.border } },
        axisLabel: { show: false, color: styles.muted },
      },
      yAxis: {
        type: 'value',
        axisLine: { lineStyle: { color: styles.border } },
        splitLine: { lineStyle: { color: styles.border, type: 'dashed' } },
        axisLabel: { color: styles.muted },
      },
      tooltip: { trigger: 'axis', backgroundColor: styles.card, borderColor: styles.border, textStyle: { color: styles.foreground } },
      legend: {
        data: ['Фраги', 'ТК'],
        textStyle: { color: styles.muted },
      },
      series: [
        {
          name: 'Фраги',
          type: 'bar',
          data: fragsValues,
          itemStyle: { color: styles.positive },
          emphasis: { focus: 'series' },
          barWidth: 18,
        },
        {
          name: 'ТК',
          type: 'bar',
          data: tkValues,
          itemStyle: { color: styles.negative },
          emphasis: { focus: 'series' },
          barWidth: 18,
        },
      ],
      animationDuration: 0,
    });
  }, [fragsChart, fragsLabels, fragsValues, tkValues, theme, fragsChart.readyTick]);

  useEffect(() => {
    if (!attendanceChartSeries.length) return;
    const { styles } = attendanceChart;
    attendanceChart.setOption({
      grid: { left: 52, right: 16, top: 32, bottom: 42 },
      xAxis: {
        type: 'category',
        data: attendanceChartLabels.slice(0, attendanceChartSeries.length),
        axisLine: { lineStyle: { color: styles.border } },
        axisLabel: { show: false, color: styles.muted },
      },
      yAxis: {
        type: 'value',
        axisLine: { lineStyle: { color: styles.border } },
        splitLine: { lineStyle: { color: styles.border, type: 'dashed' } },
        axisLabel: { color: styles.muted },
      },
      tooltip: { trigger: 'axis', backgroundColor: styles.card, borderColor: styles.border, textStyle: { color: styles.foreground } },
      series: [
        {
          type: 'bar',
          data: attendanceChartSeries,
          itemStyle: { color: styles.accent },
          barWidth: 18,
          emphasis: { focus: 'series' },
        },
      ],
      animationDuration: 0,
    });
  }, [attendanceChart, attendanceChartLabels, attendanceChartSeries, theme, attendanceChart.readyTick]);

  useEffect(() => {
    if (!contactInteractions.length) return;

    const { styles } = contactChart;
    const data = contactInteractions.flatMap((item) => [
      {
        id: `${item.opponent}-kills`,
        name: `${item.opponent}__kills`,
        displayName: `${item.opponent}: ${item.kills}`,
        value: item.kills,
        itemStyle: { color: styles.positive },
      },
      {
        id: `${item.opponent}-deaths`,
        name: `${item.opponent}__deaths`,
        displayName: `${item.opponent}: ${item.deaths}`,
        value: item.deaths,
        itemStyle: { color: styles.negative },
      },
    ]).filter((entry) => Number(entry.value) > 0);

    contactChart.setOption({
      tooltip: {
        trigger: 'item',
        backgroundColor: styles.card,
        borderColor: styles.border,
        textStyle: { color: styles.foreground },
        formatter: (params: any) => params.data?.displayName ?? `${params.name}: ${params.value ?? ''}`,
      },
      series: [
        {
          type: 'treemap',
          roam: false,
          nodeClick: false,
          breadcrumb: { show: false },
          data,
          animation: false,
          label: {
            show: true,
            formatter: (params: any) => params.data?.displayName ?? params.name,
            color: styles.foreground,
          },
          upperLabel: { show: false },
          itemStyle: {
            borderColor: styles.border,
            borderWidth: 1,
            gapWidth: 2,
          },
          emphasis: { disabled: true },
        },
      ],
      animationDuration: 0,
      animationDurationUpdate: 0,
      animation: false,
    });
  }, [contactChart, contactInteractions, theme, contactChart.readyTick]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">Карточка отряда</p>
          <h2 className="text-3xl font-bold">{squadName}</h2>
        </div>
        <button
          type="button"
          onClick={onBack}
          className="rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground transition hover:border-primary hover:text-primary"
        >
          Назад к рейтингу
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        {['summary', 'attendance'].map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key as 'summary' | 'attendance')}
            className={cn(
              'rounded-md border px-3 py-2 text-sm font-semibold transition',
              tab === key
                ? 'border-primary bg-primary/20 text-primary'
                : 'border-border bg-card text-foreground hover:border-primary/60'
            )}
          >
            {key === 'summary' ? 'Сводка' : 'Посещаемость'}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {tab === 'summary' ? (
          <motion.div key="summary" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} className="space-y-6">
            {trophies.length > 0 && (
              <div className="card p-4 space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">Кубки за место в рейтинге</p>
                  <p className="text-2xl font-semibold">Награды</p>
                </div>
                <div className="flex flex-wrap justify-center gap-3">
                  {trophies.map((award, index) => (
                    <div
                      key={`${award.title}-${award.season ?? index}`}
                      className="flex h-full w-32 flex-col items-center gap-3 rounded-lg border border-border bg-card/60 py-3 text-center"
                    >
                      <div className="space-y-1 text-sm">
                        <p className="font-semibold">{award.title}</p>
                        {award.season && <p className="text-xs text-muted-foreground">{award.season}</p>}
                      </div>
                      <div className="flex items-center justify-center">
                        <img src={award.icon} alt={award.title} title={award.description} className="h-32 w-32" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="card overflow-hidden">
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <div>
                  <p className="text-sm text-muted-foreground">Сводка показателей отряда</p>
                  <p className="text-2xl font-semibold">Сводка</p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                    {summaryColumns.map(({ key, label }) => (
                        <th key={key} className="px-3 py-3 text-center align-middle font-semibold">
                          <button
                            type="button"
                            onClick={() => {
                              if (sortKey === key) {
                                setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                                return;
                              }
                              setSortKey(key);
                              setSortDirection('desc');
                            }}
                            className="inline-flex items-center gap-1 font-semibold text-muted-foreground transition hover:text-foreground"
                          >
                            {label}
                            {sortKey === key && <span className="text-xs">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedSummaryRows.map((row, idx) => (
                      <tr
                        key={`${row.nickname}-${idx}`}
                        className={`${idx % 2 === 0 ? 'bg-card' : 'bg-card/60'} transition-colors hover:bg-muted/40`}
                      >
                        <td className="px-3 py-3 text-center align-middle font-semibold">
                          <a
                            href={`/player/${encodeURIComponent(row.nickname)}`}
                            className="inline-flex items-stretch justify-center rounded hover:underline"
                            aria-label={`Открыть статистику игрока ${row.nickname}`}
                            onClick={(event) => {
                              event.preventDefault();
                              onNavigatePlayer?.(row.nickname);
                            }}
                          >
                            {row.recordType && recordBadges[row.recordType] ? (
                              <>
                                <span
                                  className="px-2 py-1"
                                  style={{ backgroundColor: 'rgb(255, 193, 7)', color: '#0f1115' }}
                                >
                                  {row.nickname}
                                </span>
                                <span className="flex items-center justify-center px-2 py-1 bg-background dark:bg-card">
                                  <img
                                    src={recordBadges[row.recordType].icon}
                                    alt={recordBadges[row.recordType].title}
                                    title={recordBadges[row.recordType].title}
                                    className="h-6 w-6"
                                  />
                                </span>
                              </>
                            ) : (
                              <span className="px-2 py-1">{row.nickname}</span>
                            )}
                          </a>
                        </td>
                        <td className="px-3 py-3 text-center align-middle">{row.attendance}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.frags}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.tk}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.survival}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.med.toFixed(3)}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.deaths}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.tkDeaths}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.ka.toFixed(1)}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.kd.toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card p-4 space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Кого мы чаще всего убиваем и кто чаще убивает нас</p>
                <p className="text-2xl font-semibold">Баланс контактов с отрядами</p>
              </div>
              <div ref={contactChart.ref} className="h-[420px]" />
            </div>

            <div className="space-y-6">
              <div className="card p-4 space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground">КПД по играм</p>
                  <p className="text-2xl font-semibold">Динамика КПД</p>
                </div>
                <div ref={kpdChart.ref} className="h-[320px]" />
              </div>

              <div className="card p-4 space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground">Сравнение фрагов и тимкиллов по боям</p>
                  <p className="text-2xl font-semibold">Фраги и ТК</p>
                </div>
                <div ref={fragsChart.ref} className="h-[320px]" />
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div key="attendance" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} className="space-y-6">
            <div className="card p-4 space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Число игроков на каждой игре</p>
                <p className="text-2xl font-semibold">Динамика посещаемости</p>
              </div>
              <div ref={attendanceChart.ref} className="h-[320px]" />
            </div>

            <div className="card overflow-hidden">
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <div>
                  <p className="text-sm text-muted-foreground">Список игр и явка бойцов</p>
                  <p className="text-2xl font-semibold">Посещаемость</p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      {['№', 'Миссия', 'Карта', 'Сервер', 'Дата и время', 'Посещаемость'].map((col) => (
                        <th key={col} className="px-3 py-3 text-center align-middle font-semibold">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceHistory.map((row, idx) => (
                      <tr
                        key={`${row.mission}-${idx}`}
                        className={`${idx % 2 === 0 ? 'bg-card' : 'bg-card/60'} transition-colors hover:bg-muted/40`}
                      >
                        <td className="px-3 py-3 text-center align-middle font-semibold">{idx + 1}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.mission}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.map}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.server}</td>
                        <td className="px-3 py-3 text-center align-middle">{row.date}</td>
                        <td className="px-3 py-3 text-center align-middle font-semibold">{row.attendance}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
